/**
 * /es/blog — Spanish blog index. Fetches translated posts from
 * /api/translation/blog-posts/es. Falls back to a "coming soon" notice
 * if no posts are translated yet (gracefully degrades during the
 * background translation run).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import EsSEO from '@/components/seo/EsSEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SITE = 'https://fortworthdental.com';

const SpanishBlogIndex = () => {
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/translation/blog-posts/es?limit=50`)
      .then((res) => {
        if (cancelled) return;
        setPosts(res.data?.posts || []);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message);
        setPosts([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <EsSEO
        title="Noticias Dentales – Fort Worth Dental"
        description="Consejos de salud dental, guías sobre implantes, blanqueamiento y odontología cosmética en español."
        canonical="/es/blog"
        hreflangEn="/blogs"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Blog',
          inLanguage: 'es-US',
          url: `${SITE}/es/blog`,
          name: 'Noticias Dentales – Fort Worth Dental',
        }}
      />

      <section className="bg-gradient-to-br from-teal-700 via-teal-600 to-emerald-600 text-white py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Noticias Dentales</h1>
          <p className="text-lg text-teal-50 max-w-3xl">
            Consejos de salud dental, guías sobre implantes, blanqueamiento y odontología cosmética — escritos por el Dr. Mund, la Dra. Razo, el Dr. Hiwiadi y el Dr. Pham.
          </p>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          {posts === null ? (
            <p className="text-center text-gray-500" data-testid="es-blog-loading">Cargando artículos...</p>
          ) : posts.length === 0 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 max-w-2xl mx-auto text-center" data-testid="es-blog-empty">
              <p className="text-2xl font-bold text-amber-900 mb-2">📝 Próximamente</p>
              <p className="text-gray-700 mb-4">
                Estamos traduciendo cuidadosamente más de 50 artículos sobre salud dental al español. Mientras tanto, lee todos los artículos en inglés.
              </p>
              <div className="flex flex-wrap justify-center gap-3 mt-4">
                <Link to="/blogs" className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-6 py-2.5 rounded-md">
                  Ver Blog en Inglés
                </Link>
                <Link to="/es/contacto" className="bg-white border-2 border-teal-600 text-teal-600 font-semibold px-6 py-2.5 rounded-md hover:bg-teal-50">
                  Contactar Dentista
                </Link>
              </div>
              {error && <p className="text-xs text-red-500 mt-4">{error}</p>}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="es-blog-grid">
              {posts.map((p) => (
                <Link
                  key={p.es_slug || p.en_slug}
                  to={`/es/blog/${p.es_slug || p.en_slug}`}
                  className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
                  data-testid={`es-blog-card-${p.es_slug || p.en_slug}`}
                >
                  <div
                    className="aspect-video bg-gradient-to-br from-teal-600 via-teal-500 to-emerald-500 overflow-hidden relative flex items-center justify-center"
                    data-testid={`es-blog-thumb-${p.es_slug || p.en_slug}`}
                  >
                    {p.featured_image && (
                      <img
                        src={p.featured_image}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <svg className="w-12 h-12 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m3.75 13.5h3.375m-3.375 0L9.75 18M3.375 7.5h17.25c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125H3.375A1.125 1.125 0 012.25 18.375v-9.75c0-.621.504-1.125 1.125-1.125z" />
                      </svg>
                    </div>
                  </div>
                  <div className="p-5">
                    {p.category && (
                      <span className="inline-block text-xs font-semibold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full mb-2">
                        {p.category}
                      </span>
                    )}
                    <h2 className="text-lg font-bold text-gray-900 group-hover:text-teal-700 mb-2 line-clamp-2">
                      {p.title}
                    </h2>
                    {p.summary && (
                      <p className="text-sm text-gray-600 line-clamp-3">{p.summary}</p>
                    )}
                    {p.published_at && (
                      <p className="text-xs text-gray-400 mt-3">
                        {new Date(p.published_at).toLocaleDateString('es-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default SpanishBlogIndex;
